import type { TradingCoin } from "@shared/api";

const ICON_CACHE_KEY = "crypto_icons_cache";
const ICON_CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours (icons change less frequently)
const ICON_CACHE_VERSION = 2; // Incremented to force reload after Solana icon update
const MAX_CACHE_SIZE = 100; // Maximum number of cached icons

interface CachedIcon {
  symbol: string;
  dataUrl: string; // base64 or blob URL
  timestamp: number;
  sourceUrl: string; // original Supabase URL for comparison
  loadTime: number; // performance tracking
}

interface IconCacheData {
  icons: Map<string, CachedIcon>;
  timestamp: number;
  version: number;
}

class IconCacheManager {
  private cache = new Map<string, CachedIcon>();
  private isInitialized = false;
  private loadingPromises = new Map<string, Promise<string>>();

  constructor() {
    this.initialize();
  }

  private initialize(): void {
    if (typeof window === "undefined" || this.isInitialized) return;

    try {
      this.loadFromStorage();
      this.isInitialized = true;
    } catch (error) {

      this.clear();
    }
  }

  private loadFromStorage(): void {
    const cached = localStorage.getItem(ICON_CACHE_KEY);
    if (!cached) return;

    const data = JSON.parse(cached);
    const isExpired = Date.now() - data.timestamp > ICON_CACHE_DURATION;
    const isStaleVersion = data.version !== ICON_CACHE_VERSION;

    if (isExpired || isStaleVersion) {
      this.clear();
      return;
    }

    // Convert array back to Map (JSON doesn't preserve Map structure)
    if (data.icons && Array.isArray(data.icons)) {
      this.cache = new Map(data.icons);
    }
  }

  private saveToStorage(): void {
    if (typeof window === "undefined") return;

    try {
      const data: Omit<IconCacheData, 'icons'> & { icons: [string, CachedIcon][] } = {
        icons: Array.from(this.cache.entries()),
        timestamp: Date.now(),
        version: ICON_CACHE_VERSION,
      };

      localStorage.setItem(ICON_CACHE_KEY, JSON.stringify(data));
    } catch (error) {

    }
  }

  private async fetchIconAsDataUrl(url: string, symbol: string): Promise<string> {
    const startTime = performance.now();

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const blob = await response.blob();
      const dataUrl = await this.blobToDataUrl(blob);
      const loadTime = performance.now() - startTime;

      // Cache the result
      const cachedIcon: CachedIcon = {
        symbol: symbol.toUpperCase(),
        dataUrl,
        timestamp: Date.now(),
        sourceUrl: url,
        loadTime,
      };

      this.cache.set(symbol.toUpperCase(), cachedIcon);
      this.maintainCacheSize();
      this.saveToStorage();

      return dataUrl;
    } catch (error) {
      throw error;
    }
  }

  private blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error("Failed to convert blob to data URL"));
      reader.readAsDataURL(blob);
    });
  }

  private maintainCacheSize(): void {
    if (this.cache.size <= MAX_CACHE_SIZE) return;

    // Remove oldest entries first
    const sortedEntries = Array.from(this.cache.entries())
      .sort((a, b) => a[1].timestamp - b[1].timestamp);

    const toRemove = sortedEntries.slice(0, this.cache.size - MAX_CACHE_SIZE);
    toRemove.forEach(([key]) => this.cache.delete(key));
  }

  /**
   * Get cached icon or fetch and cache if not available
   */
  async getIcon(coin: TradingCoin): Promise<string> {
    if (!coin.image_url || !coin.symbol) {
      throw new Error("Invalid coin data: missing image_url or symbol");
    }

    const symbol = coin.symbol.toUpperCase();
    const cached = this.cache.get(symbol);

    // Return cached version if valid and URL hasn't changed
    if (cached && cached.sourceUrl === coin.image_url) {
      const isExpired = Date.now() - cached.timestamp > ICON_CACHE_DURATION;
      if (!isExpired) {
        return cached.dataUrl;
      }
    }

    // Check if already loading to prevent duplicate requests
    const existingPromise = this.loadingPromises.get(symbol);
    if (existingPromise) {
      return existingPromise;
    }

    // Fetch and cache new icon
    const loadPromise = this.fetchIconAsDataUrl(coin.image_url, symbol);
    this.loadingPromises.set(symbol, loadPromise);

    try {
      const result = await loadPromise;
      return result;
    } finally {
      this.loadingPromises.delete(symbol);
    }
  }

  /**
   * Batch preload icons for multiple coins
   */
  async preloadIcons(coins: TradingCoin[]): Promise<void> {
    const toLoad = coins.filter(coin => {
      if (!coin.image_url || !coin.symbol) return false;

      const symbol = coin.symbol.toUpperCase();
      const cached = this.cache.get(symbol);

      if (!cached) return true;
      if (cached.sourceUrl !== coin.image_url) return true;

      const isExpired = Date.now() - cached.timestamp > ICON_CACHE_DURATION;
      return isExpired;
    });

    if (toLoad.length === 0) {
      return;
    }

    // Load in small batches to avoid overwhelming the browser
    const BATCH_SIZE = 3;
    for (let i = 0; i < toLoad.length; i += BATCH_SIZE) {
      const batch = toLoad.slice(i, i + BATCH_SIZE);
      const batchPromises = batch.map(coin =>
        this.getIcon(coin).catch(() => {

          return null;
        })
      );

      await Promise.all(batchPromises);

      // Small delay between batches to prevent rate limiting
      if (i + BATCH_SIZE < toLoad.length) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
  }

  /**
   * Check if icon is cached and valid
   */
  isCached(coin: TradingCoin): boolean {
    if (!coin.symbol || !coin.image_url) return false;

    const cached = this.cache.get(coin.symbol.toUpperCase());
    if (!cached) return false;
    if (cached.sourceUrl !== coin.image_url) return false;

    const isExpired = Date.now() - cached.timestamp > ICON_CACHE_DURATION;
    return !isExpired;
  }

  /**
   * Get cache statistics for monitoring
   */
  getStats() {
    const icons = Array.from(this.cache.values());
    const now = Date.now();
    const averageLoadTime = icons.length > 0
      ? icons.reduce((sum, icon) => sum + icon.loadTime, 0) / icons.length
      : 0;

    return {
      totalCached: this.cache.size,
      maxCacheSize: MAX_CACHE_SIZE,
      cacheUsage: (this.cache.size / MAX_CACHE_SIZE) * 100,
      averageLoadTime: Math.round(averageLoadTime * 100) / 100,
      expiredCount: icons.filter(icon => now - icon.timestamp > ICON_CACHE_DURATION).length,
      activeLoads: this.loadingPromises.size,
    };
  }

  /**
   * Clear all cached icons
   */
  clear(): void {
    this.cache.clear();
    this.loadingPromises.clear();
    if (typeof window !== "undefined") {
      localStorage.removeItem(ICON_CACHE_KEY);
    }
  }

  /**
   * Remove specific icon from cache
   */
  remove(symbol: string): boolean {
    const removed = this.cache.delete(symbol.toUpperCase());
    if (removed) {
      this.saveToStorage();
    }
    return removed;
  }
}

// Export singleton instance
export const iconCache = new IconCacheManager();

// Export types for external use
export type { CachedIcon };